import { useEffect, useRef, useState } from 'react';
import logger, { LogCategory } from '@/lib/logging';
import { drawProcessedFrame, type FrameProcessing } from './frame-processing';

const PROCESSED_FPS = 30;

/**
 * The stream the tools consume. With mirror and crop off it is the raw share (no extra work);
 * otherwise frames are redrawn through a canvas (crop region, horizontal flip) and captured as a
 * new stream. The raw share stays owned by the page.
 */
export function useProcessedStream(raw: MediaStream | null, processing: FrameProcessing): MediaStream | null {
  const [processed, setProcessed] = useState<MediaStream | null>(null);
  const active = !!raw && (processing.mirror || !!processing.crop);
  const { mirror, crop } = processing;
  // The draw loop reads the latest crop from a ref; cropKey restarts the stream when it changes.
  const cropRef = useRef(crop);
  cropRef.current = crop;
  const cropKey = crop ? `${crop.x},${crop.y},${crop.w},${crop.h}` : '';

  useEffect(() => {
    if (!raw || !active) {
      setProcessed(null);
      return;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const capture = (canvas as HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }).captureStream;
    if (!ctx || !capture) {
      logger.warn('RemoteAssessment: canvas capture unavailable, using the unprocessed share', {
        category: LogCategory.CAMERA,
      });
      setProcessed(null);
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = raw;
    void video.play().catch(() => undefined);
    let frame = 0;
    const draw = () => {
      drawProcessedFrame(ctx, canvas, video, { mirror, crop: cropRef.current });
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    const out = capture.call(canvas, PROCESSED_FPS);
    setProcessed(out);

    return () => {
      cancelAnimationFrame(frame);
      out.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    };
  }, [raw, active, mirror, cropKey]);

  // No processed stream (not ready yet, or no canvas capture): fall back to the raw share rather
  // than leaving both tools without a picture.
  return active && processed ? processed : raw;
}
